import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videoCategories } from '@/mocks/categories';
import { videos, type VideoItem } from '@/mocks/videos';
import { Link } from 'react-router-dom';

function VideoCard({ video }: { video: VideoItem }) {
  return (
    <Link
      to={`/article/${video.id}`}
      className="group bg-background-100 rounded-lg overflow-hidden border border-background-300/30 hover:border-primary-500/30 transition-all cursor-pointer"
    >
      <div className="relative overflow-hidden aspect-video">
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 rounded text-xs text-foreground-200 whitespace-nowrap">
          {video.duration}
        </div>
        <div className="absolute top-2 left-2 px-2 py-0.5 bg-primary-500/90 rounded text-xs text-background-50 dark:text-foreground-950 font-medium whitespace-nowrap">
          {video.quality}
        </div>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-full shadow-lg">
            <i className="ri-play-fill text-background-50 dark:text-foreground-950 text-xl"></i>
          </div>
        </div>
      </div>
      <div className="p-3 md:p-4">
        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
          {video.title}
        </h3>
        <div className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
          <span className="flex items-center gap-1">
            <i className="ri-star-fill text-accent-500 text-xs"></i>
            {video.rating}
          </span>
          <span>{video.views.toLocaleString()}次</span>
          <span>{video.source}</span>
        </div>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const category = useMemo(() => videoCategories.find(c => c.slug === slug), [slug]);

  useEffect(() => {
    if (slug && !category) {
      navigate('/not-found', { replace: true });
    }
  }, [slug, category, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  if (!category) return null;

  const categoryVideos = videos.filter(v => v.category === slug);

  const sortedVideos = [...categoryVideos].sort((a, b) => {
    if (sortBy === 'popular') return b.views - a.views;
    if (sortBy === 'rating') return b.rating - a.rating;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  const catNameMap: Record<string, string> = {
    dianying: '电影',
    dianshiju: '电视剧',
    zongyi: '综艺',
    dongman: '动漫',
    jilupian: '纪录片',
    shaoer: '少儿',
  };

  const categoryName = catNameMap[slug || ''] || category.name;

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 妖精视频`,
    description: category.description,
    url: `https://yaojingvideo.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '妖精视频',
      url: 'https://yaojingvideo.com',
    },
  };

  return (
    <>
      <SEO
        title={`${categoryName} - 妖精视频免费在线观看高清${categoryName}全集`}
        description={`妖精视频${categoryName}频道，${category.description}，高清${categoryName}全集免费在线观看，不缓冲即点即播。`}
        keywords={`${categoryName},免费${categoryName},高清${categoryName},在线观看${categoryName},妖精视频`}
        canonicalUrl={`https://yaojingvideo.com/category/${slug}`}
        schema={pageSchema}
      />

      <Navbar />

      <main>
        <div className="bg-gradient-to-b from-background-100 to-background-50">
          <div className="container-site py-8 md:py-12">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />

            <div className="flex items-center gap-4 mt-2">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-background-200 rounded-xl">
                <i className={`${category.icon} text-primary-500 text-2xl md:text-3xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                  {categoryName}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-6 overflow-x-auto pb-2">
              <span className="text-xs text-foreground-500 flex-shrink-0 whitespace-nowrap">排序：</span>
              {[
                { key: 'latest' as const, label: '最新发布' },
                { key: 'popular' as const, label: '最多播放' },
                { key: 'rating' as const, label: '最高评分' },
              ].map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-4 py-2 text-sm rounded-full transition-all whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-200 text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="container-site py-8 md:py-10">
          {sortedVideos.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {sortedVideos.map(video => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>

              {sortedVideos.length > 8 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium cursor-pointer whitespace-nowrap">1</button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:text-foreground-900 text-sm transition-colors cursor-pointer whitespace-nowrap">2</button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:text-foreground-900 text-sm transition-colors cursor-pointer whitespace-nowrap">3</button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:text-foreground-900 text-sm transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-200 rounded-full">
                <i className="ri-video-line text-foreground-500 text-3xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无视频内容</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}